import { useEffect, useState } from "react"

import { PageHeader } from "@/components/layout"
import { useLocale, useT } from "@/i18n/provider"
import { api, ApiError, ensureActiveWorkspace } from "@/lib/api"
import type { BillingPlans, QuotaStatus, Workspace } from "@/lib/types"
import { getActiveWorkspaceId } from "@/lib/workspace"

export function BillingPage() {
  const t = useT()
  const { locale } = useLocale()
  const [plans, setPlans] = useState<BillingPlans | null>(null)
  const [quota, setQuota] = useState<QuotaStatus | null>(null)
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([api.billingPlans(), api.billingMe()])
      .then(([p, q]) => {
        setPlans(p)
        setQuota(q)
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : t("billing.loadFailed")))
  }, [t])

  // Which workspace this page bills - /billing/me is already scoped to the
  // active workspace via X-Workspace-Id (see api.ts's authHeaders), so this
  // is purely a label; a failure here never blocks the billing data above.
  useEffect(() => {
    ensureActiveWorkspace()
      .then(() => api.listWorkspaces())
      .then((list) => {
        const id = getActiveWorkspaceId()
        setWorkspace(list.find((w) => w.id === id) ?? list[0] ?? null)
      })
      .catch(() => {
        /* best effort: the page still works without the workspace name */
      })
  }, [])

  if (error) return <div className="p-6 text-sm text-destructive">{error}</div>
  if (!plans || !quota)
    return <div className="text-muted-foreground p-6 text-sm">{t("common.loading")}</div>

  const pct = Math.min(100, Math.round((quota.tokens_used / quota.token_cap) * 100))
  const fmt = (n: number) => n.toLocaleString(locale)
  const balanceStr = `$${(quota.balance_micro_usd / 1_000_000).toFixed(2)}`

  return (
    <>
      <PageHeader
        title={t("billing.title")}
        description={workspace ? t("billing.workspaceLabel", { name: workspace.name }) : undefined}
      />
      <div className="mx-auto max-w-3xl space-y-8 p-6">
        <section
          className={`rounded-lg border p-5 ${quota.needs_top_up ? "border-destructive" : ""}`}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t("billing.balanceTitle")}</h2>
            <span
              className={`text-2xl font-bold ${quota.needs_top_up ? "text-destructive" : ""}`}
            >
              {balanceStr}
            </span>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">{t("billing.balanceHint")}</p>
          {quota.needs_top_up && (
            <div className="mt-3 rounded-md bg-destructive/10 p-3 text-sm">
              <p className="text-destructive font-medium">{t("billing.topUpCta")}</p>
              {plans.payment.card_number && (
                <p className="mt-2">
                  {t("billing.upgradeBefore")}{" "}
                  <span className="font-mono">{plans.payment.card_number}</span>
                  {t("billing.upgradeMiddle")}{" "}
                  <span className="font-medium">{plans.payment.admin_contact}</span>{" "}
                  {t("billing.upgradeAfter")}
                </p>
              )}
            </div>
          )}
        </section>

        <section className="rounded-lg border p-5">
          <h2 className="text-lg font-semibold">
            {t("billing.yourPlan", { plan: quota.plan_label })}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {t("billing.renewsOn", { date: new Date(quota.period_end).toLocaleDateString(locale) })}
          </p>
          <div className="mt-4">
            <div className="flex justify-between text-sm">
              <span>{t("billing.tokens")}</span>
              <span>
                {fmt(quota.tokens_used)} / {fmt(quota.token_cap)}
              </span>
            </div>
            <div className="mt-1 h-2 w-full rounded bg-muted">
              <div className="h-2 rounded bg-primary" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-3 text-sm">
              {t("billing.agentsUsage", { used: quota.agents_used, max: quota.max_agents })}
            </div>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-3">
          {plans.plans.map((plan) => (
            <div
              key={plan.key}
              className={`rounded-lg border p-4 ${plan.key === quota.plan_key ? "border-primary ring-1 ring-primary" : ""}`}
            >
              <h3 className="font-semibold">{plan.label}</h3>
              <p className="mt-1 text-2xl font-bold">
                {plan.price_uzs === 0
                  ? t("billing.free")
                  : `${fmt(plan.price_uzs)} ${plans.payment.currency}`}
              </p>
              <ul className="text-muted-foreground mt-3 space-y-1 text-sm">
                <li>
                  {t("billing.tokenCapPeriod", {
                    count: plan.token_cap,
                    formatted: fmt(plan.token_cap),
                  })}
                </li>
                <li>{t("billing.planAgents", { count: plan.max_agents })}</li>
              </ul>
            </div>
          ))}
        </section>

        {plans.payment.card_number && (
          <section className="rounded-lg border bg-muted/40 p-5 text-sm">
            <h3 className="font-semibold">{t("billing.upgradeTitle")}</h3>
            <p className="mt-2">
              {t("billing.upgradeBefore")}{" "}
              <span className="font-mono">{plans.payment.card_number}</span>
              {t("billing.upgradeMiddle")}{" "}
              <span className="font-medium">{plans.payment.admin_contact}</span>{" "}
              {t("billing.upgradeAfter")}
            </p>
          </section>
        )}
      </div>
    </>
  )
}
